import api from '../api/api';

// Types
export interface AttendanceStatus {
  faceRegistered: boolean;
  date: string;
  hasAttendance: boolean;
  checkInTime?: string;
  checkOutTime?: string;
  isCheckedIn: boolean;
  isComplete: boolean;
  status?: 'PRESENT' | 'ABSENT' | 'HALF_DAY' | 'LATE';
  workingMinutes?: number;
  workingHours?: string;
  vendorLocation?: {
    latitude: number | null;
    longitude: number | null;
    radiusMeters: number;
    configured: boolean;
  };
  approvalStatus?: 'AUTO_APPROVED' | 'PENDING_APPROVAL' | 'MANUALLY_APPROVED' | 'REJECTED';
  pendingApproval?: boolean;
  flags?: string[];
}

export interface AttendanceRecord {
  id: number;
  date: string;
  checkInTime?: string;
  checkOutTime?: string;
  status: string;
  workingMinutes?: number;
  workingHours?: string;
  isComplete: boolean;
}

export interface AttendanceHistory {
  records: AttendanceRecord[];
  startDate: string;
  endDate: string;
  totalDays: number;
}

export interface FaceVerificationResult {
  matched: boolean;
  confidence: number;
  message: string;
}

export interface LocationVerificationResult {
  withinGeofence: boolean;
  distanceMeters: number;
  message: string;
}

export interface CheckInResponse {
  success: boolean;
  message: string;
  checkInTime: string;
  faceMatchConfidence: number;
}

export interface CheckOutResponse {
  success: boolean;
  message: string;
  checkOutTime: string;
  workingMinutes: number;
  workingHours: string;
  faceMatchConfidence?: number;
  pendingApproval?: boolean;
  approvalStatus?: 'AUTO_APPROVED' | 'PENDING_APPROVAL' | 'MANUALLY_APPROVED' | 'REJECTED';
  verificationIssues?: string[];
}

// API calls
export const attendanceApi = {
  // Get today's attendance status
  getTodayStatus: async (): Promise<AttendanceStatus> => {
    const response = await api.get('/api/steward/attendance/today');
    return response.data;
  },

  // Get attendance history
  getHistory: async (startDate?: string, endDate?: string): Promise<AttendanceHistory> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/api/steward/attendance/history?${params.toString()}`);
    return response.data;
  },

  // Check if face is registered
  getFaceStatus: async (): Promise<{ faceRegistered: boolean }> => {
    const response = await api.get('/api/steward/attendance/face/status');
    return response.data;
  },

  // Register face embedding
  registerFace: async (embedding: number[]): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/api/steward/attendance/face/register', { embedding });
    return response.data;
  },

  // Verify face (for testing)
  verifyFace: async (embedding: number[]): Promise<FaceVerificationResult> => {
    const response = await api.post('/api/steward/attendance/face/verify', { embedding });
    return response.data;
  },

  // Verify location (for testing)
  verifyLocation: async (latitude: number, longitude: number): Promise<LocationVerificationResult> => {
    const response = await api.post('/api/steward/attendance/location/verify', { latitude, longitude });
    return response.data;
  },

  // Check in with face and location
  checkIn: async (faceEmbedding: number[], latitude: number, longitude: number): Promise<CheckInResponse> => {
    const response = await api.post('/api/steward/attendance/check-in', {
      faceEmbedding,
      latitude,
      longitude
    });
    return response.data;
  },

  // Check out with face and location verification
  checkOut: async (faceEmbedding: number[], latitude: number, longitude: number): Promise<CheckOutResponse> => {
    const response = await api.post('/api/steward/attendance/check-out', {
      faceEmbedding,
      latitude,
      longitude
    });
    return response.data;
  }
};

// Attendance configuration
export interface AttendanceConfig {
  locationRequired: boolean;
  faceRequired: boolean;
  faceThreshold: number;
  autoApprove: boolean;
  capturePhoto: boolean;
  geofenceRadius: number;
  locationConfigured: boolean;
}

export interface PendingApproval {
  id: number;
  stewardId: number;
  stewardName: string;
  stewardCode: string;
  date: string;
  checkInTime: string;
  faceMatchConfidence: number;
  checkInDistanceMeters: number;
  flags: string[];
  checkInPhotoUrl?: string;
}

export interface AuditLog {
  id: number;
  actionType: string;
  reason: string;
  createdAt: string;
  stewardId: number;
  stewardName: string;
  performedBy: string;
  attendanceId?: number;
  attendanceDate?: string;
  previousValue?: string;
  newValue?: string;
}

export interface FlagOption {
  value: string;
  description: string;
}

// Vendor API calls for reports
export const vendorStewardReportApi = {
  // Get steward performance report
  getPerformanceReport: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/api/vendor/steward-reports/performance?${params.toString()}`);
    return response.data;
  },

  // Get attendance report
  getAttendanceReport: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/api/vendor/steward-reports/attendance?${params.toString()}`);
    return response.data;
  },

  // Get today's attendance status for all stewards
  getTodayAttendance: async () => {
    const response = await api.get('/api/vendor/steward-reports/today');
    return response.data;
  },

  // Set venue location for geofencing
  setVenueLocation: async (latitude: number, longitude: number, radiusMeters?: number) => {
    const response = await api.post('/api/vendor/steward-reports/location', {
      latitude,
      longitude,
      radiusMeters: radiusMeters || 100
    });
    return response.data;
  },

  // Get venue location settings
  getVenueLocation: async () => {
    const response = await api.get('/api/vendor/steward-reports/location');
    return response.data;
  },

  // Get attendance configuration
  getConfig: async (): Promise<AttendanceConfig> => {
    const response = await api.get('/api/vendor/steward-reports/config');
    return response.data;
  },

  // Update attendance configuration
  updateConfig: async (config: Partial<AttendanceConfig>) => {
    const response = await api.post('/api/vendor/steward-reports/config', config);
    return response.data;
  },

  // Get pending approvals
  getPendingApprovals: async (): Promise<{ pendingApprovals: PendingApproval[]; count: number }> => {
    const response = await api.get('/api/vendor/steward-reports/pending-approvals');
    return response.data;
  },

  // Approve attendance
  approveAttendance: async (attendanceId: number, notes?: string) => {
    const response = await api.post(`/api/vendor/steward-reports/approve/${attendanceId}`, { notes });
    return response.data;
  },

  // Reject attendance
  rejectAttendance: async (attendanceId: number, reason: string) => {
    const response = await api.post(`/api/vendor/steward-reports/reject/${attendanceId}`, { reason });
    return response.data;
  },

  // ===== ADMIN ACTIONS =====

  // Reset check-in
  resetCheckIn: async (attendanceId: number, reason: string) => {
    const response = await api.post(`/api/vendor/steward-reports/attendance/${attendanceId}/reset-check-in`, { reason });
    return response.data;
  },

  // Reset check-out
  resetCheckOut: async (attendanceId: number, reason: string) => {
    const response = await api.post(`/api/vendor/steward-reports/attendance/${attendanceId}/reset-check-out`, { reason });
    return response.data;
  },

  // Add flag to attendance
  addFlag: async (attendanceId: number, flag: string, notes?: string) => {
    const response = await api.post(`/api/vendor/steward-reports/attendance/${attendanceId}/flags`, { flag, notes });
    return response.data;
  },

  // Remove flag from attendance
  removeFlag: async (attendanceId: number, flag: string) => {
    const response = await api.delete(`/api/vendor/steward-reports/attendance/${attendanceId}/flags/${encodeURIComponent(flag)}`);
    return response.data;
  },

  // Get flags for attendance
  getFlags: async (attendanceId: number) => {
    const response = await api.get(`/api/vendor/steward-reports/attendance/${attendanceId}/flags`);
    return response.data;
  },

  // Reset face embedding for steward
  resetFace: async (stewardId: number, reason: string) => {
    const response = await api.post(`/api/vendor/steward-reports/steward/${stewardId}/reset-face`, { reason });
    return response.data;
  },

  // Get steward face status
  getStewardFaceStatus: async (stewardId: number) => {
    const response = await api.get(`/api/vendor/steward-reports/steward/${stewardId}/face-status`);
    return response.data;
  },

  // Get flag options
  getFlagOptions: async (): Promise<{ flagOptions: FlagOption[] }> => {
    const response = await api.get('/api/vendor/steward-reports/flag-options');
    return response.data;
  },

  // ===== AUDIT HISTORY =====

  // Get audit history for vendor
  getAuditHistory: async (limit: number = 50): Promise<{ auditLogs: AuditLog[]; count: number }> => {
    const response = await api.get(`/api/vendor/steward-reports/audit-history?limit=${limit}`);
    return response.data;
  },

  // Get audit history for specific attendance
  getAttendanceAuditHistory: async (attendanceId: number): Promise<{ attendanceId: number; auditLogs: AuditLog[]; count: number }> => {
    const response = await api.get(`/api/vendor/steward-reports/attendance/${attendanceId}/audit-history`);
    return response.data;
  }
};
